import {
 Button,
 Card,
 Checkbox,
 Form,
 Input,
 InputNumber,
 Popover,
 Spin,
 Switch,
} from 'antd'
import ListContentLayout from 'layout/ListContentLayout'
import React, { FC, useEffect, useState } from 'react'
import { TLeadEngagement, TSmartAlertPost } from 'features/automation/types'
import {
 useGetLeadEngagementAlertQuery,
 useGetSmartListingAlertQuery,
 useSmartLeadEngagementStoreMutation,
 useSmartListingStoreMutation,
} from 'features/automation/api'
import LeadEngagementItem, {
 LeadEngagementNotification,
} from './LeadEngagementItem'
import SmartEngegementCard from '../components/SmartEngegementCard'

const initialValues: TLeadEngagement = {}

const LeadEngagementAlert: FC = () => {
 const [SmartLeadEngagementStore, { isLoading }] =
  useSmartLeadEngagementStoreMutation()
 const [SmartLeadEngagementStoreAll, { isLoading: isLoadingAll }] =
  useSmartLeadEngagementStoreMutation()
 const [status, setStatus] = useState(false)
 const {
  data,
  refetch,
  isLoading: dataLoading,
 } = useGetLeadEngagementAlertQuery(null)
 const [form] = Form.useForm<TLeadEngagement>()

 const wache = Form.useWatch([], form)

 useEffect(() => {
  if (data) {
   form.setFieldsValue({
    ...initialValues,
    ...data,
   })
   setStatus(data.status === 1)
  } else {
   form.setFieldsValue(initialValues)
  }
 }, [data])

 const handleUpdateStatus = async (val: boolean) => {
  setStatus(val)
  const values = form.getFieldsValue()
  const postData: TLeadEngagement = {
   ...values,
   status: val ? 1 : 0,
  }
  await SmartLeadEngagementStore(postData).unwrap()
  refetch()
 }

 const handleUpdateChanges = async () => {
  const values = await form.validateFields()
  const postData: TLeadEngagement = {
   ...values,
   status: status ? 1 : 0,
  }
  await SmartLeadEngagementStoreAll(postData).unwrap()
  refetch()
 }

 return (
  <ListContentLayout title="Lead Engagement Alert">
   <div className="smart-engegement-page">
    <div className="smart-engegement-page-body">
     <Spin spinning={dataLoading}>
      <Form form={form} initialValues={initialValues}>
       <div className="smart-listing-page">
        <div className="smart-listing-main-header">
         <h2>
          Lead Engagement Alert
          <Popover
           content={
            <div
             style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '10px',
              minWidth: '300px',
              maxWidth: '590px',
              margin: 0,
              padding: '16px',
             }}
            >
             AgentRoof CRM provides comprehensive options to track lead
             engagement across your website, emails, and SMS. You can choose to
             be notified when leads become active after a set number of hours or
             days, when they interact with properties, or when they perform
             actions like scheduling viewings or booking callbacks. You can also
             track email and SMS engagement, such as when a lead clicks a link,
             unsubscribes, or replies. Notifications can be delivered via email
             or push notifications.
            </div>
           }
          >
           <Button className="ms-2" size="small" shape="circle">
            <i className="fa fa-info" />
           </Button>
          </Popover>
         </h2>
         <Switch
          disabled={isLoading}
          onChange={(v) => {
           handleUpdateStatus(v)
          }}
          checked={status}
         />
        </div>
        <div
         style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          position: 'relative',
         }}
        >
         <SmartEngegementCard title="Website Engagement">
          <LeadEngagementItem
           name="active_after_hours"
           input={<InputNumber disabled={!status} min={1} max={23} />}
           helper="(Min: 1, Max: 23)"
           label="Notify me when lead become active after"
           action={
            <LeadEngagementNotification
             disabled={!status}
             emailName="active_after_hours_email_notification"
             pushName="active_after_hours_push_notification"
            />
           }
           labelAffix="hrs"
          />
          <LeadEngagementItem
           name="active_after_days"
           input={<InputNumber disabled={!status} min={1} max={364} />}
           helper="(Min: 1, Max: 364)"
           label="Notify me when lead become active after"
           labelAffix="days"
           action={
            <LeadEngagementNotification
             disabled={!status}
             emailName="active_after_days_email_notification"
             pushName="active_after_days_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="inactive_after_days"
           input={<InputNumber disabled={!status} min={7} max={99} />}
           helper="(Min: 7, Max: 99)"
           label="Notify me when lead become inactive after"
           labelAffix="days"
           action={
            <LeadEngagementNotification
             disabled={!status}
             emailName="inactive_after_days_email_notification"
             pushName="inactive_after_days_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="same_property_views"
           input={<InputNumber disabled={!status} min={2} max={99} />}
           helper="(Min: 2, Max: 99)"
           label="Notify me when leads looks the same property"
           labelAffix="times"
           action={
            <LeadEngagementNotification
             disabled={!status}
             emailName="same_property_views_email_notification"
             pushName="same_property_views_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="different_properties_views"
           input={<InputNumber disabled={!status} min={2} max={99} />}
           helper="(Min: 2, Max: 99)"
           label="Notify me when lead looks more than"
           labelAffix="different properties"
           action={
            <LeadEngagementNotification
             disabled={!status}
             emailName="different_properties_views_email_notification"
             pushName="different_properties_views_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="subscribed_listing_alert"
           input={<CheckBoxCustom disabled={!status} readOnly />}
           label="Notify me when lead subscribed a Listing Alert"
           action={
            <LeadEngagementNotification
             disabled={!status}
             readOnly
             emailName="subscribed_listing_alert_email_notification"
             pushName="subscribed_listing_alert_push_notification"
            />
           }
          />
         </SmartEngegementCard>
         <SmartEngegementCard title="Email Engagement">
          <LeadEngagementItem
           name="clicked_email_link"
           input={<CheckBoxCustom disabled={!status} />}
           label="Notify me when lead clicked a link in email"
           getValueFromEvent={(v) => {
            form.setFieldsValue({
             clicked_email_link_email_notification: v,
             clicked_email_link_push_notification: v,
            })
            return v
           }}
           action={
            <LeadEngagementNotification
             disabled={!status}
             getEmailValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ clicked_email_link: true })
              } else if (wache.clicked_email_link_push_notification === false) {
               form.setFieldsValue({ clicked_email_link: false })
              }
              return v
             }}
             getPushValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ clicked_email_link: true })
              } else if (
               wache.clicked_email_link_email_notification === false
              ) {
               form.setFieldsValue({ clicked_email_link: false })
              }
              return v
             }}
             emailName="clicked_email_link_email_notification"
             pushName="clicked_email_link_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="opened_email"
           input={<CheckBoxCustom disabled={!status} />}
           label="Notify me when lead opened an email"
           getValueFromEvent={(v) => {
            form.setFieldsValue({
             opened_email_email_notification: v,
             opened_email_push_notification: v,
            })
            return v
           }}
           action={
            <LeadEngagementNotification
             disabled={!status}
             getEmailValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ opened_email: true })
              } else if (wache.opened_email_push_notification === false) {
               form.setFieldsValue({ opened_email: false })
              }
              return v
             }}
             getPushValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ opened_email: true })
              } else if (wache.opened_email_email_notification === false) {
               form.setFieldsValue({ opened_email: false })
              }
              return v
             }}
             emailName="opened_email_email_notification"
             pushName="opened_email_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="unsubscribed_email"
           input={<CheckBoxCustom disabled={!status} />}
           label="Notify me when lead unsubscribed email"
           getValueFromEvent={(v) => {
            form.setFieldsValue({
             unsubscribed_email_email_notification: v,
             unsubscribed_email_push_notification: v,
            })
            return v
           }}
           action={
            <LeadEngagementNotification
             disabled={!status}
             getEmailValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ unsubscribed_email: true })
              } else if (wache.unsubscribed_email_push_notification === false) {
               form.setFieldsValue({ unsubscribed_email: false })
              }
              return v
             }}
             getPushValueFromEvent={(v) => {
              if (v === true) {
               form.setFieldsValue({ unsubscribed_email: true })
              } else if (
               wache.unsubscribed_email_email_notification === false
              ) {
               form.setFieldsValue({ unsubscribed_email: false })
              }
              return v
             }}
             emailName="unsubscribed_email_email_notification"
             pushName="unsubscribed_email_push_notification"
            />
           }
          />
          <LeadEngagementItem
           name="replied_email"
           input={<CheckBoxCustom disabled={!status} readOnly />}
           label="Notify me when lead replied to email"
           action={
            <LeadEngagementNotification
             disabled={!status}
             readOnly
             emailName="replied_email_email_notification"
             pushName="replied_email_push_notification"
            />
           }
          />
         </SmartEngegementCard>
         <SmartEngegementCard title="SMS Engagement">
          <LeadEngagementItem
           name="replied_sms"
           input={<CheckBoxCustom disabled={!status} readOnly />}
           label="Notify me when lead reply to SMS"
           action={
            <LeadEngagementNotification
             disabled={!status}
             readOnly
             emailName="replied_sms_email_notification"
             pushName="replied_sms_push_notification"
            />
           }
          />
         </SmartEngegementCard>
        </div>
       </div>
      </Form>
     </Spin>
    </div>
    {status && (
     <div className="smart-engegement-page-footer">
      <Button
       shape="round"
       style={{ padding: '6px 36px', height: 'unset', fontSize: '15px' }}
       loading={isLoadingAll}
       onClick={() => {
        handleUpdateChanges()
       }}
       type="primary"
      >
       Update
      </Button>
     </div>
    )}
   </div>
  </ListContentLayout>
 )
}

export default LeadEngagementAlert

const CheckBoxCustom: FC<{
 disabled?: boolean
 readOnly?: boolean
 value?: boolean
 onChange?: (v?: boolean) => void
}> = ({ onChange, value, readOnly, disabled }) => {
 return (
  <div className="smr-checkbox-blk" data-readony={readOnly}>
   <Checkbox
    style={readOnly ? { pointerEvents: 'none' } : {}}
    disabled={disabled}
    onChange={({ target }) => {
     if (!readOnly) {
      onChange?.(target.checked)
     }
    }}
    checked={value}
    value={value}
   />
  </div>
 )
}
